import { Description, Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { Fragment, useRef, useState } from "react";

import t from "../../i18n";
import useOverlayLayer from "../../use/overlay-layer";
import Button from "./Button";

type Props = {
  cancelText?: string;
  children: React.ReactNode;
  confirmText?: string;
  loading?: "cancel" | "confirm";
  onClick?: (accept: boolean) => void;
  onExited?: () => void;
  open: boolean;
  title: string;
  type?: "info" | "success" | "warning";
};

export default function Modal(props: Props) {
  const type = props.type ?? "info";
  const buttonColor = type === "warning" ? "red" : type === "success" ? "green" : "black";
  const confirmText = props.confirmText ?? t("Ok");

  const cancelButtonRef = useRef(null);
  const [present, setPresent] = useState(props.open);
  if (props.open && !present) setPresent(true);
  const zIndex = useOverlayLayer(present);

  function onCancel() {
    props.onClick?.(false);
  }

  function onConfirm() {
    props.onClick?.(true);
  }

  function onExitComplete() {
    setPresent(false);
    props.onExited?.();
  }

  return (
    <Transition afterLeave={onExitComplete} as={Fragment} show={props.open}>
      <Dialog as="div" className="relative" initialFocus={cancelButtonRef} onClose={onCancel} style={{ zIndex }}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className="relative w-full overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:max-w-lg
                  dark:bg-neutral-800"
                data-role="modal"
              >
                <div className="space-y-2 p-5 text-center text-slate-700 dark:text-slate-100">
                  <DialogTitle className="text-xl font-semibold">{props.title}</DialogTitle>
                  {typeof props.children === "string" ? <Description>{props.children}</Description> : props.children}
                </div>
                <div className="flex flex-col justify-items-stretch gap-3 bg-neutral-100 p-3 sm:flex-row-reverse dark:bg-black/60">
                  <Button
                    className="w-full"
                    color={buttonColor}
                    disabled={props.loading === "cancel"}
                    loading={props.loading === "confirm"}
                    onClick={onConfirm}
                  >
                    {confirmText}
                  </Button>
                  {props.cancelText && (
                    <Button
                      className="w-full"
                      disabled={props.loading === "confirm"}
                      loading={props.loading === "cancel"}
                      onClick={onCancel}
                      ref={cancelButtonRef}
                      variant={buttonColor === "black" ? "secondary" : "primary"}
                    >
                      {props.cancelText}
                    </Button>
                  )}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
